import React, { useContext, useEffect, useRef, useState } from 'react';
import { APIContext } from '../contexts/APIContext';
import { fetchAccountNames, fetchIndustryNames, createNewProject, fetchProjectNames, trackUserClicks } from '../utils';
import Dropdown from './ui/Dropdown';

import { useToast } from './ui/toast/UseToast';
import { LoaderPage } from '../pages/LoaderPage';
import { Search } from 'lucide-react';
import { getValidToken } from '../utils';

interface CreateProject {
  showCreate: boolean;
  setShowCreate: (showCreate: boolean) => void;
}

interface FormData {
  projectName: string;
  accountName: string;
  industryName: string;
  startDate: string;
  endDate: string;
}
const CreateProjectModal: React.FC<CreateProject> = ({ showCreate, setShowCreate }) => {
  const {
    interviewProjectNames,
    accountNames,
    industryNames,
    websocketConnected,
    websocketConnectionRef: { current: websocket },
    accounts,
    newProjectStatus,
    setNewProjectStatus,
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
  } = useContext(APIContext);

  const [accountSelected, setAccountSelected] = useState('');
  const [newAccount, setNewAccount] = useState('');

  const [industrySelected, setIndustrySelected] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectError, setProjectError] = useState('');
  const [clientError, setClientError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const clientNames = accountNames.map((acc: { account_id: number; account_name: string }) => acc.account_name);
  const [filteredOptions, setFilteredOptions] = useState(() => clientNames);
  const projectNames = interviewProjectNames.map((project: any) => project?.project_name);
  const hasAccountName = accountSelected === 'Create New Client' ? newAccount : accountSelected;

  const [form, setForm] = useState<FormData>({
    projectName: '',
    accountName: '',
    industryName: '',
    startDate: '',
    endDate: '',
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const accountValue = accountSelected === 'Create New Client' ? newAccount : accountSelected;

  useEffect(() => {
    (async () => {
      const token = await getValidToken();
      if (websocket && websocket.state === 'Connected' && !accountNames.length) {
        // trigger fetch project details only if accountNames list is empty or not fetched.
        fetchAccountNames(accounts, websocket, token);
      }
      if (websocket && websocket.state === 'Connected' && !industryNames.length) {
        fetchIndustryNames(accounts, websocket, token);
      }
      if (websocket && websocket.state === 'Connected' && !projectNames.length) {
        fetchProjectNames(accounts, websocket, token);
      }
    })();
  }, [websocket, websocketConnected]);
  useEffect(() => {
    if (!newProjectStatus) return;

    if (newProjectStatus === 'success') {
      toast({
        status: 'success',
        description: 'Project created successfully',
      });

      setShowCreate(false); // close modal
      setShowLoader(false); // hide loader
    } else {
      toast({
        status: 'info',
        description: 'Failed to create project',
      });

      setShowLoader(false);
    }

    // reset status after handling
    setNewProjectStatus('');
  }, [newProjectStatus]);

  const handleAccountSelection = (account: string) => {
    setNewAccount('');
    setSearchText(account);
    setAccountSelected(account);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (form.projectName !== '') {
      console.log('Form....', form);
    }
  }, [form]);

  const handleOptionClick = (option: any) => {
    setSearchText(option);
    setFilteredOptions([]);
    // setIsOptionSelected(true);
  };

  const handleNewAccountName = (e: any) => {
    // e.preventDefault();
    // setNewAccount(e.target.value);
    // setIsReviewMode(true);

    e.preventDefault();
    setClientError(() => '');

    const sanitizedValue = e.target.value;
    // Validate the project name for invalid characters
    if (!validateProjectName(sanitizedValue)) {
      setProjectName('');
      setClientError('Only letters, numbers, hyphens (-), and underscores (_) are allowed.');

      return;
    }
    if (!clientNames.some((projectName: string) => projectName.toLowerCase() === sanitizedValue.toLowerCase())) {
      setNewAccount(sanitizedValue);
      setClientError('');
    } else {
      setNewAccount(sanitizedValue);
      setClientError('Client Name already exists');
    }
  };

  const handleIndustrySelection = (item: string) => {
    setIndustrySelected(item);
    // setIsReviewMode(true);
  };

  function validateProjectName(input: string) {
    const regex = /^[a-zA-Z0-9 .&_-]*$/; // Allow letters, numbers, spaces, underscores, and hyphens
    if (!regex.test(input)) {
      console.log('Special characters not allowed');

      return false; // Validation failed
    }
    return true; // Validation passed
  }
  const handleProjectNameChange = (e: any) => {
    e.preventDefault();
    setProjectError(() => '');

    const sanitizedValue = e.target.value;
    // Validate the project name for invalid characters
    if (!validateProjectName(sanitizedValue)) {
      // setProjectName('');
      setProjectError('Only letters, numbers, hyphens (-), and underscores (_) are allowed.');

      return;
    }
    if (!projectNames.some((projectName: string) => projectName.toLowerCase() === sanitizedValue.toLowerCase())) {
      setProjectName(sanitizedValue);
      setProjectError('');
    } else {
      setProjectName(sanitizedValue);
      setProjectError('Project Name already exists');
    }
  };
  const handleKeyPress = (e: any) => {
    const regex = /^[a-zA-Z0-9 _-]*$/;
    if (!regex.test(e.key)) {
      e.preventDefault();
    }
  };
  const handleStartDateChange = (e: any) => {
    const selectedStart = e.target.value;
    setStartDate(selectedStart);
    if (endDate && selectedStart >= endDate) {
      setDateError('End Date must be greater than Start Date.');
      setEndDate('');
    } else {
      setDateError('');
    }
  };

  const handleEndDateChange = (e: any) => {
    const selectedEndDate = e.target.value;
    setEndDate(selectedEndDate);

    // Validate that end date is after start date
    if (startDate && selectedEndDate <= startDate) {
      setDateError('End Date must be greater than Start Date.');
      //  setEndDate('');
    } else {
      setDateError('');
    }
  };
  const scrollToTop = () => {
    if (modalRef.current) {
      modalRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleReview = () => {
    setForm({
      projectName: projectName,
      accountName: accountValue,
      industryName: industrySelected,
      startDate: startDate,
      endDate: endDate,
    });
    setIsReviewMode(true);
    scrollToTop();
  };

  const handleSubmit = async () => {
    const token = await getValidToken();
    trackUserClicks(websocket, 'ProjectCreated', userDetails, chatSessionId, WBUId, AppName, accounts, token);
    setShowLoader(true);
    createNewProject(accounts, websocket, form, token);
  };

  return (
    <>
      <div
        className="c-modal__overlay -dismiss-modal bottom-10 "
        style={{
          position: 'fixed',
          height: '100vh',
          zIndex: 51,
          backgroundColor: 'rgba(0,0,0,0.4)',
          backdropFilter: 'none',
        }}
      ></div>
      <div
        id="projectModal"
        className="c-modal"
        role="dialog"
        tabIndex={-1}
        aria-hidden="false"
        aria-labelledby="modalDialogTitle"
        style={{ zIndex: '100', top: '1.2rem' }}
        // ref={modalRef}
      >
        <div className="c-modal__container c-create-project ">
          {/* <!-- <div className="c-create-project__wrapper"> --> */}
          <div className="c-modal__content top-9" style={{ backgroundColor: '#fff' }}>
            <div className="c-modal__header">
              <div className="c-modal__header-right">
                <div className="c-modal__title" role="heading" id="modalDialogTitle" aria-level={2}>
                  {' '}
                  Create New Project
                </div>
                {/* <!-- add className -dismiss-modal to buttons that'll close the modal --> */}
                <button
                  type="button"
                  className="c-modal__header-close -dismiss-modal"
                  aria-label="Close"
                  title="Close"
                  onClick={() => setShowCreate(false)}
                ></button>
              </div>
            </div>
            <div className="c-modal__body" ref={modalRef}>
              <div className="c-create-project__ip-wrap">
                <div className="c-create-project__ip-heading text-left">Project*</div>
                <div className={`o-input-wrap ${isReviewMode ? '-reviewmode' : ''}`}>
                  <input
                    type="text"
                    value={projectName}
                    className="o-input"
                    id="input1"
                    name="input1"
                    maxLength={250}
                    placeholder="Add Project"
                    onChange={handleProjectNameChange}
                    style={{ color: '#737373', fontSize: '.875rem' }}
                    // onKeyDown={handleKeyPress}
                  />
                </div>
                <div style={{ textAlign: 'left' }}>
                  {projectError && (
                    <span style={{ color: 'red', fontSize: '10px' }} id="error-message">
                      {projectError}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <Dropdown
                  label="Client*"
                  items={clientNames}
                  onSelect={handleAccountSelection}
                  isReviewMode={isReviewMode}
                />
              </div>
              {accountSelected === 'Create New Client' && (
                <div className="c-create-project__acc-name">
                  <div className="c-create-project__ip-heading text-left">Client Name*</div>
                  <div className={`c-create-project__acc-name-wrap ${isReviewMode ? '-reviewmode' : ''}`}>
                    <div className="o-input-wrap ">
                      <input
                        type="text"
                        value={newAccount}
                        className="o-input "
                        id="input1"
                        name="input1"
                        maxLength={250}
                        onChange={handleNewAccountName}
                        placeholder="Add Client Name"
                        // onKeyDown={handleKeyPress}
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      {clientError && (
                        <span style={{ color: 'red', fontSize: '10px' }} id="error-message">
                          {clientError}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <Dropdown
                label="Industry*"
                items={industryNames}
                onSelect={handleIndustrySelection}
                isReviewMode={isReviewMode}
              />

              <div className="c-create-project__date-wrap text-left">
                <label className="c-create-project__date-wrap-num">Project Start Date*</label>
                <div className={`c-tab-content__input-date ${isReviewMode ? '-reviewmode' : ''}`}>
                  <div className="o-input-wrap ">
                    <input
                      type="date"
                      className="o-input cursor-pointer py-[.6rem]"
                      id="startDate"
                      name="27 Nov 2024"
                      value={startDate}
                      onChange={handleStartDateChange}
                      placeholder="Type"
                      style={{ color: '#737373', fontSize: '.875rem' }}
                      // min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    {/* <button role="button" aria-label="btn"></button> */}
                  </div>
                </div>
              </div>
              <div className="c-create-project__date-wrap text-left">
                <label className="c-create-project__date-wrap-num">Project End Date*</label>
                <div className={`c-tab-content__input-date ${isReviewMode ? '-reviewmode' : ''}`}>
                  <div className="o-input-wrap !pl-0">
                    <input
                      type="date"
                      className="o-input py-[.6rem] px-0 !pl-[1rem] cursor-pointer"
                      id="endDate"
                      name="31 Dec 2025"
                      value={endDate}
                      onChange={handleEndDateChange}
                      style={{ color: '#737373', fontSize: '.875rem' }}
                      placeholder="Type"
                      // min={new Date().toISOString().split('T')[0]}
                      required
                      disabled={!startDate}
                    />
                  </div>
                  {dateError && (
                    <span style={{ color: 'red', fontSize: '10px', textAlign: 'center' }}>{dateError}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="c-modal__footer">
              <div className="c-modal__footer-btnGroup">
                {/* <!-- add className -dismiss-modal to buttons that'll close the modal --> */}
                {isReviewMode ? (
                  <button
                    type="button"
                    className="o-secondary-button -dismiss-modal"
                    aria-label="Cancel"
                    onClick={() => setIsReviewMode(false)}
                  >
                    Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    className="o-secondary-button -dismiss-modal"
                    aria-label="Cancel"
                    onClick={() => setShowCreate(false)}
                  >
                    Cancel
                  </button>
                )}
                {!isReviewMode ? (
                  <button
                    type="button"
                    className="o-primary-button "
                    aria-label="Submit"
                    disabled={
                      !dateError &&
                      !projectError &&
                      !clientError &&
                      projectName &&
                      industrySelected &&
                      startDate &&
                      endDate &&
                      hasAccountName
                        ? false
                        : true
                    }
                    onClick={handleReview}
                  >
                    Review
                  </button>
                ) : (
                  <button type="button" className="o-primary-button " aria-label="Submit" onClick={handleSubmit}>
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* <!-- </div> --> */}
      </div>
      {showLoader && <LoaderPage />}
    </>
  );
};

export default CreateProjectModal;
